import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import Button from "~/components-shared/Button";
import { StatusStateEnum } from "~/types/enums";
import { usePostHog } from "posthog-js/react";
import Modal from "~/components-shared/Modal";
import toastsActionCreators from "~/store/toasts/actions";
import { Alert } from "~/components-shared";
import InsightsForm from "~/forms/InsightsForm";
import insightsSchema from "~/forms/InsightsForm/insightsSchema";
import insightReportSelectors from "~/store/insightReports/selectors";
import insightReportsActionCreators from "~/store/insightReports/actions";
import moment from "moment";
import { helpers } from "~/lib/helpers";
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
`;
const InsightsUpdateModal = () => {
  const {
    t
  } = useTranslation();
  const report = useSelector(insightReportSelectors.getSelected);
  const reportId = report?.id || "";
  const status = useSelector(insightReportSelectors.getStatusByReport(reportId));
  const error = useSelector(insightReportSelectors.getErrorByReport(reportId));
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const defaultValues = {
    scheduleType: report?.scheduleType || undefined,
    scheduleDayOfWeek: `${report?.scheduleDayOfWeek}` || "4",
    scheduleTimeOfDay: moment.utc(report?.scheduleTimeOfDay, "hh:mma").toISOString() || "",
    scheduleDestination: report?.scheduleDestination || undefined,
    excludedTopicIds: report?.excludedTopics.map(w => w.id) || []
  };
  const {
    handleSubmit,
    control,
    reset,
    watch
  } = useForm({
    resolver: yupResolver(insightsSchema),
    defaultValues
  });
  useEffect(() => {
    if (reportId) {
      reset(defaultValues);
    }
  }, [reportId]);
  const onSubmit = handleSubmit(data => {
    dispatch(insightReportsActionCreators.updateStarted(reportId, data, _ref => {
      let {
        data: newCompany
      } = _ref;
      if (newCompany) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("InsightsUpdateModal.reportUpdated"),
          timeout: 10000
        }));
      }
    }));
    posthog.capture(`Insights Report Update – Save`, {
      ...helpers.objectDiff(defaultValues, data)
    });
  });
  const handleCancel = () => {
    dispatch(insightReportsActionCreators.actionChanged(null, null));
    posthog.capture(`Insights Report Update – Cancel`);
  };
  return /*#__PURE__*/React.createElement(Modal.Body, {
    onClose: handleCancel,
    maxWidth: "640px",
    fullWidth: true,
    title: t("InsightsUpdateModal.reportSettings")
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(InsightsForm, {
    control: control,
    watch: watch,
    report: report || undefined
  })), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: handleCancel,
    rounded: true,
    display: "inline-block"
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    display: "inline-block",
    maxWidth: "230px",
    type: "submit",
    onClick: onSubmit,
    s: {
      ml: 2
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("common.actions.save")))));
};
export default InsightsUpdateModal;